import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import {
  ContactsDefaultVisibilityColumns,
  contactsTableColumns,
  getInitialContactsTableSort,
} from "./ContactsTableUtils";
import EADataGrid from "../../../components/common/datatable/EADataGrid";
import UserDeleteConfirmDialog from "../../users/users-list/UserDeleteConfirmDialog";
import UserReinviteConfirmDialog from "../../users/users-list/UserReinviteConfirmDialog";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { UserInvitationStatus } from "services/common/Constants";
import { Context } from "states/Store";


const ContactsDataTable = forwardRef((props, ref) => {

  const {
    contacts,
    loader,
    onContactsDeleted,
    pageSize,
    multiDataGridPage,
    sheetVariant= false,
    onVisibleRowsCountChange = () => {},
  } = props;

  const [state, ] = useContext(Context);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] = useState(
    false
  );
  const [
    isReinviteContactDialogOpen,
    setIsReinviteContactDialogOpen,
  ] = useState(false);
  const [selectedContactEmail, setSelectedContactEmail] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactsColumns, setContactsColumns] = useState();
  const [columnsVisibility, setColumnsVisibility] = useState();
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [sortModel, setSortModel] = useState([]);

  useImperativeHandle(ref, () => ({
    handleReinviteContactsPopup: (user) => {
      user = {...user,
        tpAccountId: state?.thirdparty?.id,
        userId: user?.id
      }
      openReinviteContactsPopup([user?.id], user?.email);
    }
  }));

  const setContact = useCallback(
    (contactsIds, email) => {
      setSelectedContactEmail(email);
      setSelectedContacts(
        contacts
          .filter((contact) => {
            return contactsIds.includes(contact.userId)
          })
          .map((contact) => {
            return { userId: contact.userId, tpAccountId: contact.tpaccountid };
          })
      );
    },
    [contacts]
  );

  const openDeleteContactsPopup = useCallback(
    (contactsIds, email) => {
      setIsDeleteContactDialogOpen(true);
      setContact(contactsIds, email);
    },
    [setContact]
  );

  const openReinviteContactsPopup = useCallback(
    (contactsIds, email) => {
      setIsReinviteContactDialogOpen(true);
      setContact(contactsIds, email);
    },
    [setContact]
  );

  useEffect(() => {
    setContactsColumns(
      contactsTableColumns(
        openDeleteContactsPopup,
        openReinviteContactsPopup,
        hasAnyOfPrivileges
      )
    );
  }, [
    openDeleteContactsPopup,
    openReinviteContactsPopup,
    hasAnyOfPrivileges
  ]);

  useEffect(() => {
    setColumnsVisibility(ContactsDefaultVisibilityColumns());
    setSortModel(getInitialContactsTableSort());
  }, []);

  return (
    <>
      {contactsColumns && (
        <EADataGrid
          loading={loader}
          customSx={{
            "& .MuiDataGrid-columnHeader:nth-of-type(1), & .MuiDataGrid-columnHeader:nth-of-type(2)": {
              color: "#7E7E8F",
            },
          }}
          hasCustomPagination={!multiDataGridPage}
          inputRows={contacts}
          columns={contactsColumns}
          initialVisibilityModel={columnsVisibility}
          selection={false}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            if (newSortModel[0] !== sortModel[0]) {
              setSortModel(newSortModel);
            }
          }}
          isRowSelectableMethod={(params) => params.row.status !== UserInvitationStatus.REGISTERED}
          clickable={true}
          pageSize={pageSize}
          variant={sheetVariant ? "sheet" : "default"}
          onVisibleRowsCountChange={onVisibleRowsCountChange}
        />
      )}

      {isDeleteContactDialogOpen && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_CONTACT) && (
        <UserDeleteConfirmDialog
          usersIds={selectedContacts}
          userEmail={selectedContactEmail}
          isOpen={selectedContactEmail}
          handleClose={() => setIsDeleteContactDialogOpen(false)}
          handleOnDelete={onContactsDeleted}
          tpContact={true}
        />
      )}

      {isReinviteContactDialogOpen && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTACT) && (
        <UserReinviteConfirmDialog
          userId={selectedContacts}
          userEmail={selectedContactEmail}
          isOpen={isReinviteContactDialogOpen}
          handleClose={() => setIsReinviteContactDialogOpen(false)}
          tpContact={true}
        />
      )}
    </>
  );
});
export default ContactsDataTable;
